import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { List } from 'app/generated/graphql'
import { useFeatureToggleState } from 'app/hooks/useFeatureToggle'
import { useOpenCustomPageEditor } from 'app/hooks/useOpenCustomPageEditor'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import { notifyError } from 'app/services/notify.client'
import type { CustomPageJson, CustomPages } from 'app/types/entities'
import { useHandleSearchParams } from 'app/utils'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { LIST_ENTITY_TYPE, savedPageOfList } from 'lib/savedLists'

// A saved list opens on its record page as `?tab=<id>`, the way activity lists open on
// Activities: the page cut by the list's rule, with the Filters builder narrowing further. Under
// the grouped navigation the list is a List, else a saved page. Call from an `observer()` component.
export function useSavedListTab(pageType: CustomPages): {
  /** False while a `?tab=` list is still being read; the page must not show unfiltered rows meanwhile. */
  settled: boolean
  list: { page: CustomPageJson; name: string; edit: () => void } | undefined
} {
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const tab = searchParams.get(`tab`)
  const listId = tab && tab !== `all` ? tab : undefined
  // The answer is kept with the id it was asked for, so a tab change never resolves against
  // the previous list. `listNonce` asks again after the list is edited.
  const [lookup, setLookup] = useState<{ id: string; page?: CustomPageJson; list?: List }>()
  const [listNonce, setListNonce] = useState(0)
  const { enabled: fromLists, settled: toggleSettled } = useFeatureToggleState(`grouped-navigation`)
  useEffect(() => {
    if (!listId || !toggleSettled) return
    let current = true
    const read = fromLists
      ? gqlFetchOne<List>(`list`, listId, undefined, { suppressErrorToast: true }).then((list) => ({
          // Another record type's List reads as no page, the same as another type's saved page.
          page: list?.entityType === LIST_ENTITY_TYPE[pageType] ? savedPageOfList(list, pageType) : undefined,
          list,
        }))
      : gqlFetchOne<CustomPageJson>(`customPage`, listId, undefined, { suppressErrorToast: true }).then((page) => ({
          page,
          list: undefined,
        }))
    bg(
      read
        .then(({ page, list }) => {
          if (current) setLookup({ id: listId, page, list })
        })
        .catch((err) => {
          logError(`Error reading a ${pageType} list`, err)
          if (current) setLookup({ id: listId })
        }),
    )
    return () => {
      current = false
    }
  }, [listId, listNonce, toggleSettled, fromLists])
  const listSettled = !listId || lookup?.id === listId
  // Another record type's list id in this page's URL is treated as not found rather than
  // applying its rule to the wrong records.
  const listPage = listId && lookup?.id === listId && lookup.page?.page === pageType ? lookup.page : undefined
  useEffect(() => {
    // A list that is gone (deleted, another workspace's, or another type's) falls back to every record.
    if (!listId || !listSettled || listPage) return
    notifyError(`That list could not be found`)
    handleSearchParams({ tab: undefined })
  }, [listId, listSettled, listPage])
  const openCustomPageEditor = useOpenCustomPageEditor()
  const editList = () =>
    listPage &&
    openCustomPageEditor({
      customPage: listPage,
      list: lookup?.list,
      pageType,
      onSaved: () => setListNonce((nonce) => nonce + 1),
      onDeleted: () => undefined,
    })

  return {
    settled: listSettled,
    list: listPage ? { page: listPage, name: listPage.name ?? ``, edit: editList } : undefined,
  }
}
